import { useState } from "react";
import { EnvelopeSimple, PaperPlaneTilt, CheckCircle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import MarketingLayout from "../components/MarketingLayout";
import { api, formatApiError } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 4) {
      toast.error("Message must be at least 4 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingLayout dataTestId="contact-page">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF9800]">
          Get in touch
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-3 leading-tight">
          Talk to us.
        </h1>
        <p className="mt-5 text-lg text-zinc-600 max-w-2xl leading-relaxed">
          Sales questions, support issues, partnership ideas — we read every message and reply within 1 business day.
        </p>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 mt-12">
          {/* Contact card */}
          <div className="lg:col-span-2">
            <div className="bg-[#1A2332] text-white p-7 border-2 border-[#1A2332]">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF9800] mb-4">
                Reach us directly
              </div>
              <a
                href="mailto:support@ezitemizer.com"
                className="flex items-center gap-3 group"
                data-testid="contact-email-link"
              >
                <div className="h-11 w-11 bg-[#FF9800] text-[#1A2332] flex items-center justify-center flex-shrink-0">
                  <EnvelopeSimple size={20} weight="fill" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Email</div>
                  <div className="font-mono text-sm group-hover:text-[#FF9800] transition-colors truncate">
                    support@ezitemizer.com
                  </div>
                </div>
              </a>
              <div className="mt-8 pt-6 border-t border-white/10 text-xs text-white/60 leading-relaxed">
                Already an ezItemizer customer? Use the <strong className="text-white">Feedback</strong> button inside the app for faster, context-rich support.
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div
                className="bg-white border-2 border-[#FF9800] p-10 text-center"
                data-testid="contact-success"
              >
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#FFF1E0] mb-5">
                  <CheckCircle size={42} weight="fill" className="text-[#FF9800]" />
                </div>
                <div className="font-display font-black text-2xl text-[#0F172A]">
                  Message received.
                </div>
                <div className="mt-3 text-sm text-zinc-600 max-w-md mx-auto leading-relaxed">
                  Thanks for reaching out, {form.name.split(" ")[0] || "friend"}. We'll get back to you at <strong>{form.email}</strong> within 1 business day.
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="bg-white border-2 border-zinc-200 p-7 space-y-5"
                data-testid="contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-2 block">
                      Your name
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={onChange("name")}
                      maxLength={120}
                      data-testid="contact-name"
                      className="w-full border-2 border-zinc-200 focus:border-[#1A2332] focus:outline-none px-3 py-2.5 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-2 block">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange("email")}
                      data-testid="contact-email"
                      className="w-full border-2 border-zinc-200 focus:border-[#1A2332] focus:outline-none px-3 py-2.5 text-sm font-mono"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-2 block">
                    Subject <span className="text-zinc-400 font-normal normal-case">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={onChange("subject")}
                    maxLength={200}
                    data-testid="contact-subject"
                    className="w-full border-2 border-zinc-200 focus:border-[#1A2332] focus:outline-none px-3 py-2.5 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-2 block">
                    Message
                  </label>
                  <textarea
                    required
                    value={form.message}
                    onChange={onChange("message")}
                    rows={6}
                    maxLength={5000}
                    data-testid="contact-message"
                    className="w-full border-2 border-zinc-200 focus:border-[#1A2332] focus:outline-none px-3 py-2.5 text-sm leading-relaxed resize-none"
                  />
                  <div className="text-[10px] text-zinc-400 font-mono mt-1 text-right">
                    {form.message.length}/5000
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#FF9800] hover:bg-[#E68900] disabled:opacity-60 text-[#1A2332] font-bold text-xs uppercase tracking-widest px-4 py-4 transition-colors"
                >
                  {submitting ? (
                    <>
                      <Spinner size={14} weight="bold" className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt size={14} weight="fill" />
                      Send message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
